import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

const Wrapper = styled.div`
  background: ${props => props.background || "rgba(240, 240, 240, 1)"};
  border-radius: 15px;
  width: 100%;
  height: 100%;
  position: relative;
  box-sizing: border-box;
  font-family: ${props => props.fontFamily || "inherit"}, sans-serif;

  .title {
    text-align: center;
    font-size: 1.5rem;
    color: ${props => props.textColor || "#334455"};
    background: ${props => props.titleBackground || "#f0f0f0"};
    padding: 10px 0;
    border-radius: 10px 10px 0 0;
  }

  .flip-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: top;
    height: calc(100% - 60px);
    overflow: hidden;
    position: relative;
    margin: 10px;
  }

  .flip-card {
    perspective: 1000px;
    margin: 10px 0;
    width: 100%;
    max-width: 300px;
  }

  .flip-card-inner {
    position: relative;
    width: 100%;
    height: 120px;
    text-align: center;
    transition: transform 0.6s;
    transform-style: preserve-3d;
  }

  .flip-card.flipping .flip-card-inner {
    transform: rotateY(180deg);
  }

  .flip-card-front,
  .flip-card-back {
    position: absolute;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid transparent;
    border-radius: 10px;
    padding: 15px;
    box-sizing: border-box;
  }

  .flip-card-front {
    background: ${props => props.neonBackground || "rgba(0, 0, 0, 0.7)"};
    color: ${props => props.neonText || "#ffffff"};
    border: 3px solid ${props => `linear-gradient(to right, ${props.neonColor1 || "#ff007f"}, ${props.neonColor2 || "#00e0ff"})`};
  }

  .flip-card-back {
    background: #f5f5f5;
    color: #333;
    transform: rotateY(180deg);
    font-size: 1rem;
    font-weight: bold;
  }

  .birthday-card {
    background: url("https://img.freepik.com/premium-photo/whimsical-birthday-cake-with-lit-candles-colorful-sprinkles-against-soft-pink-background_1293074-87805.jpg") center center / cover no-repeat;
    color: black;
    padding: 20px;
    margin-bottom: 15px;
    border-radius: 10px;
    box-shadow: 0 0 10px ${props => props.birthdayColor1 || "#ff007f"};
  }
`;

const getDynamicFontSize = (text) => {
  const wordCount = text.split(" ").length;
  
  // לדוגמה: לכל 10 מילים נוסיף/נוריד קצת בגודל
  // אפשר כמובן ליצור לוגיקה מתוחכמת יותר
  if (wordCount <= 5) return "1.4rem";
  if (wordCount <= 15) return "1.2rem";
  if (wordCount <= 30) return "1rem";
  return "0.8rem";
};

const FlipMessageTicker = ({
  initialMessages,
  birthdayMessages = [],
  background,
  textColor,
  titleBackground,
  neonBackground,
  neonText,
  neonColor1,
  neonColor2,
  birthdayColor1,
  birthdayColor2,
  fontFamily,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipping, setFlipping] = useState(false);
  const containerRef = useRef(null);
  const [groupSize, setGroupSize] = useState(1);

  const intervalDuration = 10000; // 10 seconds

  useEffect(() => {
    const updateGroupSize = () => {
      if (containerRef.current) {
        const wordsLimit = 100; // Limit words displayed at once
        let totalWords = 0;
        let tempGroupSize = 0;
        initialMessages.some((msg, index) => {
          const wordsInMessage = msg.messageDetails.text.split(' ').length;
          if (totalWords + wordsInMessage > wordsLimit) return true;
          totalWords += wordsInMessage;
          tempGroupSize++;
        });
        setGroupSize(tempGroupSize);
      }
    };

    window.addEventListener("resize", updateGroupSize);
    updateGroupSize();

    return () => window.removeEventListener("resize", updateGroupSize);
  }, [initialMessages]);

  useEffect(() => {
    if (initialMessages.length <= groupSize) return; // No rotation if messages fit within one group

    const cycleMessages = () => {
      setFlipping(true);
      setTimeout(() => {
        setFlipping(false);
        setCurrentIndex((prevIndex) =>
          prevIndex + groupSize >= initialMessages.length ? 0 : prevIndex + groupSize
        );
      }, 600);
    };

    const intervalId = setInterval(cycleMessages, intervalDuration);

    return () => clearInterval(intervalId);
  }, [groupSize, initialMessages]);

  const currentMessages = Array.isArray(initialMessages)
    ? initialMessages.slice(currentIndex, currentIndex + groupSize)
    : [];

  return (
    <Wrapper
      background={background}
      textColor={textColor}
      titleBackground={titleBackground}
      neonBackground={neonBackground}
      neonText={neonText}
      neonColor1={neonColor1}
      neonColor2={neonColor2}
      birthdayColor1={birthdayColor1}
      birthdayColor2={birthdayColor2}
      fontFamily={fontFamily}
    >
      <div className="title">הודעות</div>
      <div className="flip-container" ref={containerRef}>
        {birthdayMessages.map((birthday, index) => (
          <div
            key={`birthday-${index}`}
            className="birthday-card"
            birthdayColor1={birthdayColor1}
            birthdayColor2={birthdayColor2}
          >
            🎉 {birthday.messageDetails.greeting}
          </div>
        ))}
        {currentMessages.map((message, index) => (
          <div
            key={`message-${index}`}
            className={`flip-card ${flipping ? "flipping" : ""}`}
          >
            <div className="flip-card-inner">
              <div className="flip-card-front">
                {message.messageDetails.text}
              </div>
              <div className="flip-card-back">הודעה הבאה</div>
            </div>
          </div>
        ))}
      </div>
    </Wrapper>
  );
};

FlipMessageTicker.propTypes = {
  initialMessages: PropTypes.array.isRequired,
  birthdayMessages: PropTypes.array,
  background: PropTypes.string,
  textColor: PropTypes.string,
  titleBackground: PropTypes.string,
  neonBackground: PropTypes.string,
  neonText: PropTypes.string,
  neonColor1: PropTypes.string,
  neonColor2: PropTypes.string,
  birthdayColor1: PropTypes.string,
  birthdayColor2: PropTypes.string,
  fontFamily: PropTypes.string,
};

FlipMessageTicker.defaultProps = {
  initialMessages: [],
  birthdayMessages: [],
  background: "rgba(240, 240, 240, 1)",
  textColor: "#334455",
  titleBackground: "#f0f0f0",
  neonBackground: "rgba(0, 0, 0, 0.7)",
  neonText: "#ffffff",
  neonColor1: "#ff007f",
  neonColor2: "#00e0ff",
  birthdayColor1: "#ff007f",
  birthdayColor2: "#00e0ff",
  fontFamily: "inherit",
};

export default FlipMessageTicker;
